import { mergeAttributes } from '@tiptap/core'
import { Figure } from '../Figure/Figure.js'
import { Quote } from './Quote/Quote.js'
import { QuoteCaption } from './QuoteCaption/QuoteCaption.js'

declare module '@tiptap/core' {
  // eslint-disable-next-line no-unused-vars
  interface Commands<ReturnType> {
    blockquoteFigure: {
      setBlockquote: () => ReturnType
    }
  }
}

export const BlockquoteFigure = Figure.extend({
  name: 'blockquoteFigure',

  group: 'block',

  content: 'quote quoteCaption',

  isolating: true,

  addExtensions() {
    return [Quote, QuoteCaption]
  },

  renderHTML({ HTMLAttributes }) {
    return ['figure', mergeAttributes(HTMLAttributes, { 'data-type': this.name }), ['div', {}, 0]]
  },

  addKeyboardShortcuts() {
    return {
      Enter: () => false,
      Backspace: ({ editor }) => {
        const { state } = editor
        const { selection } = state
        const { $from } = selection

        let blockquoteFigureNode = null
        let blockquoteFigureDepth = -1

        // Look deeper in the hierarchy - the BlockquoteFigure might be at a higher level
        for (let i = $from.depth; i >= 0; i--) {
          const node = $from.node(i)
          if (node.type.name === 'blockquoteFigure') {
            blockquoteFigureNode = node
            blockquoteFigureDepth = i
            break
          }
        }

        if (!blockquoteFigureNode) {
          // Also check parent nodes that might not be in the depth range
          let pos = $from.pos
          while (pos > 0) {
            const node = editor.state.doc.nodeAt(pos)
            if (node && node.type.name === 'blockquoteFigure') {
              blockquoteFigureNode = node
              break
            }
            pos = editor.state.doc.resolve(pos).before()
            if (pos === 0) break
          }
        }

        if (!blockquoteFigureNode) {
          return false
        }

        // Check if we're at the start of the current node
        const isAtStart = $from.parentOffset === 0

        // Find the quote and caption nodes within the BlockquoteFigure
        const quoteNode = blockquoteFigureNode.firstChild
        const captionNode = blockquoteFigureNode.lastChild

        // Check if both quote and caption are empty
        const isQuoteEmpty =
          quoteNode &&
          (quoteNode.content.size === 0 ||
            (quoteNode.content.size === 2 &&
              quoteNode.firstChild?.type.name === 'paragraph' &&
              quoteNode.firstChild.content.size === 0))

        const isCaptionEmpty = captionNode && captionNode.content.size === 0

        // Delete if we're at the start and both quote and caption are empty
        if (isAtStart && isQuoteEmpty && isCaptionEmpty) {
          const pos = $from.before(blockquoteFigureDepth)
          return editor.chain().setNodeSelection(pos).deleteSelection().run()
        }

        return false
      },
    }
  },

  addAttributes() {
    return {
      ...this.parent?.(),
    }
  },

  addCommands() {
    return {
      setBlockquote:
        () =>
        ({ state, chain }) => {
          const position = state.selection.$from.start()
          const selectionContent = state.selection.content()

          return chain()
            .focus()
            .insertContent({
              type: this.name,
              content: [
                {
                  type: 'quote',
                  content: selectionContent.content.toJSON() || [
                    {
                      type: 'paragraph',
                      attrs: {
                        textAlign: 'left',
                      },
                    },
                  ],
                },
                {
                  type: 'quoteCaption',
                },
              ],
            })
            .focus(position + 1)
            .run()
        },
    }
  },
})

export default BlockquoteFigure
